import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Activity, Calendar as CalIcon, Radio, Rocket, History, ShieldCheck, KeyRound, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";

const TABS = [
  { id: "watchlist", label: "WATCHLIST", icon: CalIcon },
  { id: "radar",     label: "LIVE RADAR", icon: Radio },
  { id: "ipo",       label: "IPO WATCH", icon: Rocket },
  { id: "history",   label: "HISTORY", icon: History },
];

// ── Deterministic formatting helpers (pure, no external calls) ────────
function fmtIso(v) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (isNaN(d.getTime())) return "—";
    return d.toISOString().replace("T", " ").replace(".000Z", "Z");
  } catch { return "—"; }
}

function fmtEpoch(v) {
  if (v === null || v === undefined) return "—";
  const n = typeof v === "number" ? v : Number(v);
  if (!isFinite(n)) return "—";
  try {
    return new Date(n * 1000).toISOString()
      .replace("T", " ").replace(".000Z", "Z");
  } catch { return "—"; }
}

// Detection latency (canonical: first_detected_at - provider_published_at).
// Returns null on any missing / malformed input — the UI shows "—".
function computeLatencyMs(publishedIso, detectedIso) {
  if (!publishedIso || !detectedIso) return null;
  const pub = Date.parse(publishedIso);
  const det = Date.parse(detectedIso);
  if (!isFinite(pub) || !isFinite(det)) return null;
  const delta = det - pub;
  if (delta < 0) return null;   // ingestion strictly cannot precede publish
  return delta;
}

function fmtLatency(ms) {
  if (ms === null || ms === undefined) return "—";
  if (ms < 1000) return `+${ms}ms`;
  if (ms < 10000) return `+${(ms / 1000).toFixed(1)}s`;
  if (ms < 60000) return `+${Math.round(ms / 1000)}s`;
  const m = Math.floor(ms / 60000);
  const s = Math.round((ms % 60000) / 1000);
  return `+${m}m ${String(s).padStart(2, "0")}s`;
}

// Derive UI-safe health category from diag response.
// Returns one of:
//   'RADAR_OFF' | 'NEWS_DISABLED' | 'UNHEALTHY' | 'LIVE'
function categorizeNewsHealth(diag) {
  if (!diag) return "UNHEALTHY";
  const radar = !!diag.production_enabled;
  if (!radar) return "RADAR_OFF";
  const feed = diag.news_provider?.state;
  const worker = diag.workers?.news_ws?.state;
  const newsFlag = !!diag.flags?.CATALYST_NEWS_ENABLED;
  if (!newsFlag) return "NEWS_DISABLED";
  if (feed === "DISABLED" || worker === "DISABLED") return "NEWS_DISABLED";
  if (worker === "LIVE" && (feed === "LIVE" || feed === "DEGRADED")) return "LIVE";
  return "UNHEALTHY";
}

// ── Sub-components ────────────────────────────────────────────────────
function OperationalEmptyState({ diag }) {
  const ws = diag?.workers?.news_ws || {};
  const subs = Array.isArray(ws.subscribed_symbols)
    ? ws.subscribed_symbols.join(", ") : "—";
  return (
    <div className="grid-cell !p-4 mono text-[11px] space-y-2"
         data-testid="catalyst-waiting-state">
      <div className="flex items-center gap-2">
        <span className="chip !text-[9px] text-signal-green border-signal-green/40">
          NEWS · LIVE
        </span>
        <span className="text-muted-foreground uppercase text-[10px] tracking-widest">
          Waiting for Catalyst events
        </span>
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
        <dt className="text-muted-foreground">News feed</dt>
        <dd className="text-foreground">{diag?.news_provider?.state || "—"}</dd>
        <dt className="text-muted-foreground">Subscription</dt>
        <dd className="text-foreground" data-testid="waiting-subscription">{subs}</dd>
        <dt className="text-muted-foreground">Connected since</dt>
        <dd className="text-foreground" data-testid="waiting-connected-at">
          {fmtEpoch(ws.connected_at)}
        </dd>
        <dt className="text-muted-foreground">Events this session</dt>
        <dd className="text-foreground" data-testid="waiting-events-count">
          {ws.events_received ?? "—"}
        </dd>
        <dt className="text-muted-foreground">Last event received</dt>
        <dd className="text-foreground" data-testid="waiting-last-event">
          {fmtEpoch(ws.last_event_at)}
        </dd>
        <dt className="text-muted-foreground">Reconnects</dt>
        <dd className="text-foreground" data-testid="waiting-reconnects">
          {ws.reconnect_count ?? "—"}
        </dd>
      </dl>
      <div className="text-muted-foreground text-[10px] italic">
        Listening for market-moving news…
      </div>
    </div>
  );
}

function HealthBanner({ kind, diag }) {
  const map = {
    RADAR_OFF:      { color: "muted-foreground", label: "RADAR DISABLED",
                       hint: "Set CATALYST_RADAR_ENABLED=true in deployment secrets." },
    NEWS_DISABLED:  { color: "muted-foreground", label: "NEWS FEED DISABLED",
                       hint: "CATALYST_NEWS_ENABLED is off — enable to start ingesting." },
    UNHEALTHY:      { color: "signal-yellow",    label: "NEWS FEED UNHEALTHY",
                       hint: `worker=${diag?.workers?.news_ws?.state || "?"} · feed=${diag?.news_provider?.state || "?"}` },
  };
  const cfg = map[kind];
  if (!cfg) return null;
  return (
    <div className={`grid-cell !p-3 mono text-[11px] border-${cfg.color}/40 bg-${cfg.color}/5`}
         data-testid={`catalyst-health-${kind.toLowerCase()}`}>
      <div className={`text-${cfg.color} uppercase tracking-widest`}>{cfg.label}</div>
      <div className="text-muted-foreground text-[10px] mt-1">{cfg.hint}</div>
    </div>
  );
}

function fmtReturn(n) {
  if (n === null || n === undefined) return null;
  const v = Number(n);
  if (!isFinite(v)) return null;
  const sign = v >= 0 ? "+" : "";
  return `${sign}${v.toFixed(2)}%`;
}

function OutcomeSection({ outcome }) {
  if (!outcome) {
    return (
      <div className="pt-2 border-t border-border/40 text-[10px] text-muted-foreground mono"
           data-testid="ev-outcome-empty">
        OUTCOME SINCE DETECTION · <span className="text-signal-yellow">PENDING</span>
      </div>
    );
  }
  const status = outcome.measurement_status || "PENDING";
  const cell = (n) => {
    const price = outcome[`price_${n}m`];
    const ret = outcome[`return_${n}m_pct`];
    if (price === null || price === undefined) {
      const label = status === "PENDING" ? "PENDING" : "—";
      return <span data-testid={`ev-outcome-${n}m`}>+{n}M {label}</span>;
    }
    const r = fmtReturn(ret);
    const tone = ret >= 0 ? "text-signal-green" : "text-signal-red";
    return (
      <span data-testid={`ev-outcome-${n}m`}>
        +{n}M <span className={tone}>{r}</span>
      </span>
    );
  };
  const maxR = fmtReturn(outcome.max_return_60m_pct);
  const minR = fmtReturn(outcome.min_return_60m_pct);
  return (
    <div className="pt-2 border-t border-border/40" data-testid="ev-outcome-section">
      <div className="flex items-center justify-between mb-1">
        <span className="label-cap">Outcome since detection</span>
        <span className={`chip !text-[9px] ${
          status === "COMPLETE"    ? "text-signal-green border-signal-green/40"
          : status === "PARTIAL"    ? "text-signal-yellow border-signal-yellow/40"
          : status === "UNAVAILABLE" ? "text-muted-foreground border-border/60"
          : "text-signal-yellow border-signal-yellow/40"
        }`} data-testid="ev-outcome-status">{status}</span>
      </div>
      <div className="flex gap-3 flex-wrap text-[10px] mono text-muted-foreground">
        {cell(5)} {cell(15)} {cell(30)} {cell(60)}
        {maxR && <span data-testid="ev-outcome-max">MAX +60M <span className="text-signal-green">{maxR}</span></span>}
        {minR && <span data-testid="ev-outcome-dd">MAX DD <span className="text-signal-red">{minR}</span></span>}
      </div>
      {outcome.baseline_price && (
        <div className="text-[10px] mono text-muted-foreground mt-1">
          Baseline ${Number(outcome.baseline_price).toFixed(2)}
          {outcome.baseline_bar_timestamp && (
            <> · bar {outcome.baseline_bar_timestamp.replace("T"," ").replace("Z","Z")}</>
          )}
        </div>
      )}
    </div>
  );
}

function EventCard({ e, outcome }) {
  const published = e.created_at;
  const detected = e.first_detected_at || e.news_first_detected_at
                    || e.momentum_first_detected_at;
  const latencyMs = computeLatencyMs(published, detected);
  const priceAt = e.momentum_trigger_price ?? e.price_at_detection ?? null;
  const company = e.company_name || null;   // present only when persisted
  const cp5 = e.checkpoint_plus_5m ?? null;
  const cp15 = e.checkpoint_plus_15m ?? null;
  const cp30 = e.checkpoint_plus_30m ?? null;
  return (
    <div className="grid-cell !p-3 mono text-[11px] space-y-2"
         data-testid={`catalyst-event-card-${e.id || e.provider_event_id}`}>
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-foreground font-medium text-[12px]"
              data-testid="ev-symbol">{e.symbol || "—"}</span>
        {company && (
          <span className="text-muted-foreground text-[10px]"
                 data-testid="ev-company">{company}</span>
        )}
        <span className="chip !text-[9px] text-signal-green border-signal-green/40 ml-1"
              data-testid="ev-detector">{e.detector || "NEWS_FIRST"}</span>
        <span className="ml-auto text-muted-foreground text-[10px]"
              data-testid="ev-latency">
          {latencyMs === null ? "DETECTED —"
                                : `DETECTED ${fmtLatency(latencyMs)}`}
        </span>
      </div>
      {e.headline && (
        <div className="text-foreground text-[11px]" data-testid="ev-headline">
          {e.headline}
        </div>
      )}
      {(e.detector === "MOMENTUM_FIRST" || e.detector === "MULTI_SIGNAL")
        && e.momentum_signal_components && (
        <div className="text-[10px] mono text-muted-foreground grid grid-cols-3 md:grid-cols-6 gap-x-3"
             data-testid="ev-momentum-components">
          {e.momentum_score != null && (
            <div>SCORE <span className="text-signal-green">{Math.round(e.momentum_score)}</span></div>
          )}
          {e.momentum_signal_components.return_1m_pct != null && (
            <div>1M {e.momentum_signal_components.return_1m_pct.toFixed(2)}%</div>
          )}
          {e.momentum_signal_components.return_3m_pct != null && (
            <div>3M {e.momentum_signal_components.return_3m_pct.toFixed(2)}%</div>
          )}
          {e.momentum_signal_components.return_5m_pct != null && (
            <div>5M {e.momentum_signal_components.return_5m_pct.toFixed(2)}%</div>
          )}
          {e.momentum_signal_components.relative_volume != null && (
            <div>RVOL {e.momentum_signal_components.relative_volume.toFixed(1)}×</div>
          )}
          {e.momentum_signal_components.volume_zscore != null && (
            <div>VOLZ {e.momentum_signal_components.volume_zscore.toFixed(1)}</div>
          )}
          {e.momentum_signal_components.vwap_deviation_pct != null && (
            <div>VWAP {e.momentum_signal_components.vwap_deviation_pct.toFixed(1)}%</div>
          )}
        </div>
      )}
      {e.detector === "MULTI_SIGNAL" && (
        <div className="text-[10px] mono border-l-2 border-signal-yellow/60 pl-2 space-y-0.5"
             data-testid="ev-multi-timeline">
          {e.momentum_first_detected_at && (
            <div>{fmtIso(e.momentum_first_detected_at)} · Momentum detected</div>
          )}
          {e.news_provider_published_at && (
            <div>{fmtIso(e.news_provider_published_at)} · News published</div>
          )}
          {e.news_first_detected_at && (
            <div>{fmtIso(e.news_first_detected_at)} · News detected</div>
          )}
          <div className="text-signal-yellow uppercase tracking-widest">
            signal_sequence: {e.signal_sequence || "MULTI_SIGNAL"}
          </div>
        </div>
      )}
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
        <dt className="text-muted-foreground">Source</dt>
        <dd className="text-foreground" data-testid="ev-source">
          {e.source || e.provider || "—"}
        </dd>
        <dt className="text-muted-foreground">Provider published</dt>
        <dd className="text-foreground" data-testid="ev-published">
          {fmtIso(published)}
        </dd>
        <dt className="text-muted-foreground">WingWise first detected</dt>
        <dd className="text-foreground" data-testid="ev-detected">
          {fmtIso(detected)}
        </dd>
        <dt className="text-muted-foreground">Price at detection</dt>
        <dd className="text-foreground" data-testid="ev-price">
          {priceAt !== null ? `$${Number(priceAt).toFixed(2)}` : "—"}
        </dd>
      </dl>
      <div className="flex gap-3 text-[10px] text-muted-foreground pt-1 border-t border-border/40"
           data-testid="ev-outcomes-legacy" style={{display:"none"}}>
        <span>+5M —</span><span>+15M —</span><span>+30M —</span>
      </div>
      <OutcomeSection outcome={outcome} />
    </div>
  );
}

// ── Stage 2.4 · Catalyst Market Data readiness card ────────────────
function FeedReadinessCard({ readiness, onVerify, busy, lastError }) {
  if (!readiness) return null;
  const s = readiness.current_state || "SIP_NOT_CONFIGURED";
  // Stage 2.7B: prefer backend readiness_banner when present.
  const banner = readiness.readiness_banner || null;
  const stateColor = ({
    SIP_VERIFIED:              "text-signal-green border-signal-green/40",
    SIP_ENTITLED_UNVERIFIED:   "text-signal-yellow border-signal-yellow/40",
    SIP_STALE:                 "text-signal-yellow border-signal-yellow/40",
    SIP_SUBSCRIPTION_REQUIRED: "text-signal-red border-signal-red/40",
    SIP_AUTH_FAILED:           "text-signal-red border-signal-red/40",
    SIP_PROVIDER_ERROR:        "text-signal-red border-signal-red/40",
    SIP_UNAVAILABLE:           "text-muted-foreground border-border/60",
    SIP_NOT_CONFIGURED:        "text-muted-foreground border-border/60",
  })[s] || "text-muted-foreground border-border/60";
  const lv = readiness.last_verification || {};
  // Stage 2.9A — prefer runtime ``capability_status`` (canonical
  // three-gate projection) and only fall back to the static policy
  // matrix for legacy shapes.
  const capStatus = readiness.capability_status || {};
  const capPolicy = readiness.capability_policy?.capability_matrix || {};
  const statusOf = (k) => capStatus[k]
    || capPolicy[k]?.status
    || "—";
  const capable = readiness.production_capable;
  const featureOn = !!readiness.catalyst_owner_activation;
  const authorized = !!readiness.production_activation_authorized;
  const detectorChip = featureOn
    ? (authorized ? "MOMENTUM DETECTOR: ENABLED"
                  : "MOMENTUM DETECTOR: ENABLED · SHADOW")
    : "MOMENTUM DETECTOR: DISABLED";
  const bannerText = banner || (capable
    ? "READY_FOR_OWNER_ACTIVATION"
    : "BLOCKED_PENDING_SIP");
  return (
    <div className="grid-cell !p-4 mono text-[11px] space-y-3"
         data-testid="catalyst-feed-readiness-card">
      <div className="flex items-center gap-2">
        <ShieldCheck size={13} className="text-[#0A84FF]" />
        <span className="uppercase tracking-widest text-[10px]">
          Catalyst Market Data
        </span>
        <span className={`chip !text-[9px] ml-auto ${stateColor}`}
              data-testid="feed-readiness-state">{s}</span>
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
        <dt className="text-muted-foreground">Required realtime feed</dt>
        <dd className="text-foreground uppercase"
            data-testid="feed-required">
          {readiness.capability_policy?.required_realtime_feed || "—"}
        </dd>
        <dt className="text-muted-foreground">Realtime SIP verified</dt>
        <dd className="text-foreground"
            data-testid="feed-realtime-verified">
          {readiness.realtime_sip_verified ? "YES" : "NO"}
        </dd>
        <dt className="text-muted-foreground">Owner activation flag</dt>
        <dd className="text-foreground"
            data-testid="feed-owner-activation">
          {readiness.catalyst_owner_activation ? "ON" : "OFF"}
        </dd>
        <dt className="text-muted-foreground">Last verified (UTC)</dt>
        <dd className="text-foreground"
            data-testid="feed-last-verified">
          {lv.started_at_utc || "—"}
        </dd>
        <dt className="text-muted-foreground">Verification TTL</dt>
        <dd className="text-foreground">
          {readiness.verification_ttl_hours ?? "—"}h
        </dd>
        <dt className="text-muted-foreground">Entitlement / Stream data</dt>
        <dd className="text-foreground"
            data-testid="feed-entitlement-vs-stream">
          {lv.entitlement_verified ? "ENTITLED" : "—"} /{" "}
          {lv.stream_data_verified ? "STREAM_OK" : "—"}
        </dd>
      </dl>
      <div className="flex items-center gap-2">
        <span className={`chip !text-[9px] ${capable
          ? "text-signal-green border-signal-green/40"
          : "text-signal-red border-signal-red/40"}`}
          data-testid="feed-production-readiness">
          {bannerText}
        </span>
        <span className="chip !text-[9px] text-muted-foreground border-border/60"
              data-testid="feed-momentum-lock">
          {detectorChip}
        </span>
        <button
          onClick={onVerify}
          disabled={busy}
          className={`chip cursor-pointer ml-auto ${busy
            ? "text-muted-foreground border-border/60"
            : "text-[#0A84FF] border-[#0A84FF]"}`}
          data-testid="verify-sip-readiness-btn">
          {busy ? "VERIFYING…" : "Verify SIP Readiness"}
        </button>
      </div>
      {(s === "SIP_SUBSCRIPTION_REQUIRED" || s === "SIP_NOT_CONFIGURED") && (
        <div className="text-muted-foreground italic text-[10px]"
             data-testid="feed-subscription-hint">
          Realtime SIP entitlement is required (e.g., Alpaca Algo
          Trader Plus, or a qualifying Alpaca program). WingWise
          never purchases or upgrades subscriptions. Obtain
          entitlement in the Alpaca dashboard, then retry Verify.
        </div>
      )}
      {lastError && (
        <div className="text-signal-red text-[10px]"
             data-testid="feed-verify-error">{lastError}</div>
      )}
      {/* Stage 2.9B.1 — SIP ENTITLEMENT vs LIVE STREAM. The manual
          verify probe proves ENTITLEMENT (TTL-bound). The Momentum
          worker continuously proves LIVE STREAM. Either alone
          satisfies realtime SIP readiness. */}
      <div className="pt-2 border-t border-border/40 text-[10px] space-y-1"
           data-testid="feed-live-sip-block">
        <div className="flex items-center gap-2">
          <span className="uppercase tracking-widest text-muted-foreground text-[9px]">
            Realtime SIP Evidence
          </span>
          <span className={`chip !text-[9px] ${
            readiness.readiness_source === "MANUAL_AND_LIVE"
              ? "text-signal-green border-signal-green/40"
              : readiness.readiness_source === "LIVE_STREAM"
                ? "text-[#0A84FF] border-[#0A84FF]/50"
                : readiness.readiness_source === "MANUAL"
                  ? "text-signal-yellow border-signal-yellow/40"
                  : "text-signal-red border-signal-red/40"}`}
                data-testid="readiness-source-chip">
            {readiness.readiness_source || "—"}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          <span className="text-muted-foreground">SIP entitlement</span>
          <span className="text-foreground"
                data-testid="sip-entitlement-status">
            {readiness.sip_entitlement_verified
              ? (readiness.manual_verification_stale
                  ? "VERIFIED · MANUAL PROBE STALE (>24h)"
                  : "VERIFIED")
              : "UNVERIFIED"}
          </span>
          <span className="text-muted-foreground">
            Last successful verify
          </span>
          <span className="text-foreground"
                data-testid="sip-last-success-at">
            {readiness.sip_entitlement_verified_at || "—"}
          </span>
          {readiness.last_verification_attempt && (
            <>
              <span className="text-muted-foreground">
                Last attempt
              </span>
              <span className="text-foreground"
                    data-testid="sip-last-attempt">
                {readiness.last_verification_attempt.started_at_utc}
                {" · "}
                {readiness.last_verification_attempt.readiness_state ===
                  "SIP_CONNECTION_LIMIT_CONFLICT"
                  ? "CONNECTION CONFLICT — EXISTING LIVE SIP STREAM"
                  : readiness.last_verification_attempt.readiness_state}
                {readiness.last_verification_attempt.provider_error_code
                  ? ` · ${readiness.last_verification_attempt.provider_error_code}`
                  : ""}
              </span>
            </>
          )}
          <span className="text-muted-foreground">Live SIP stream</span>
          <span className={readiness.live_sip_ready
                            ? "text-signal-green" : "text-muted-foreground"}
                data-testid="live-sip-stream-status">
            {readiness.live_sip_ready
              ? "LIVE"
              : (readiness.live_sip_reason || "—")}
          </span>
          <span className="text-muted-foreground">Resolved feed</span>
          <span className="text-foreground uppercase"
                data-testid="live-sip-resolved-feed">
            {readiness.live_sip?.resolved_feed || "—"}
            {readiness.live_sip?.entitlement
              ? ` · ${readiness.live_sip.entitlement}` : ""}
            {readiness.live_sip?.coverage
              ? ` · ${readiness.live_sip.coverage}` : ""}
          </span>
          <span className="text-muted-foreground">Worker state</span>
          <span className="text-foreground"
                data-testid="live-sip-worker-state">
            {readiness.live_sip?.worker_state || "—"}
            {readiness.live_sip?.market_session
              ? ` · ${readiness.live_sip.market_session}` : ""}
          </span>
          <span className="text-muted-foreground">Last live data</span>
          <span className="text-foreground"
                data-testid="live-sip-last-event">
            {readiness.live_sip?.last_event_at
              ? new Date(readiness.live_sip.last_event_at * 1000)
                  .toISOString().replace("T", " ").replace(".000Z", "Z")
              : "—"}
            {readiness.live_sip?.bars_received != null
              ? ` · ${readiness.live_sip.bars_received} bars`
              : ""}
          </span>
        </div>
      </div>
      <div className="text-[10px] text-muted-foreground pt-2 border-t border-border/40">
        <div>Historical Catalyst analytics: <span className="text-foreground">
          {statusOf("historical_catalyst_analytics")}
        </span></div>
        <div>Research / preview: <span className="text-foreground">
          {statusOf("research_preview")}
        </span></div>
        <div>Realtime Momentum alerts: <span className="text-foreground"
             data-testid="cap-realtime-momentum-alerts">
          {statusOf("realtime_momentum_alerts")}
        </span></div>
        <div>Human-approved execution: <span className="text-foreground"
             data-testid="cap-human-approved-execution">
          {statusOf("human_approved_execution")}
        </span></div>
        <div>Autonomous execution: <span className="text-foreground">
          {statusOf("autonomous_execution")}
        </span></div>
      </div>
    </div>
  );
}

// ── Stage 2.7B · Production Authorization Card (owner-gated) ─────
function ProductionAuthorizationCard({ activation, isOwner, onOpenAuthorize, onRevoke, revokeBusy }) {
  if (!activation) return null;
  const authorized = activation.production_activation_authorized;
  const capable = activation.production_capable;
  const banner = activation.readiness_banner || "BLOCKED";
  const cur = activation.current_authorization || null;
  const chip = ({
    PRODUCTION_CAPABLE: "text-signal-green border-signal-green/40",
    READY_FOR_OWNER_AUTHORIZATION: "text-[#0A84FF] border-[#0A84FF]/50",
    SIP_ENTITLED_STREAM_NOT_YET_VERIFIED: "text-signal-yellow border-signal-yellow/40",
    REVALIDATION_REQUIRED: "text-signal-yellow border-signal-yellow/40",
    BLOCKED_PENDING_SIP: "text-signal-red border-signal-red/40",
    BLOCKED: "text-muted-foreground border-border/60",
  })[banner] || "text-muted-foreground border-border/60";
  const showReview = isOwner && banner === "READY_FOR_OWNER_AUTHORIZATION";
  const showRevoke = isOwner && authorized;
  return (
    <div className="grid-cell !p-4 mono text-[11px] space-y-3"
         data-testid="production-authorization-card">
      <div className="flex items-center gap-2">
        <KeyRound size={13} className="text-[#0A84FF]" />
        <span className="uppercase tracking-widest text-[10px]">Production Authorization</span>
        <span className={`chip !text-[9px] ml-auto ${chip}`}
              data-testid="production-authorization-banner">{banner}</span>
      </div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
        <dt className="text-muted-foreground">Realtime SIP</dt>
        <dd data-testid="pa-realtime-sip">{activation.realtime_sip_verified ? "VERIFIED" : "—"}</dd>
        <dt className="text-muted-foreground">Stream data</dt>
        <dd data-testid="pa-stream-data">{activation.stream_data_verified ? "VERIFIED" : "—"}</dd>
        <dt className="text-muted-foreground">SIP age (s)</dt>
        <dd>{activation.sip_verification_age_seconds != null ? Math.round(activation.sip_verification_age_seconds) : "—"}</dd>
        <dt className="text-muted-foreground">Stage 2.6B validation</dt>
        <dd>{activation.validation_passed ? "PASS" : "—"}</dd>
        <dt className="text-muted-foreground">Detector policy match</dt>
        <dd data-testid="pa-policy-match">{authorized ? (activation.policy_fingerprint_matches ? "MATCHED" : "MISMATCH") : "—"}</dd>
        <dt className="text-muted-foreground">Feature config gate</dt>
        <dd>{activation.feature_config_gate ? "ON" : "OFF"}</dd>
        <dt className="text-muted-foreground">Environment</dt>
        <dd>{activation.environment}</dd>
        <dt className="text-muted-foreground">Authorization</dt>
        <dd data-testid="pa-authorization-status">{authorized ? "AUTHORIZED" : "NOT AUTHORIZED"}</dd>
        <dt className="text-muted-foreground">Authorized by</dt>
        <dd data-testid="pa-authorized-by">{cur?.authorized_by_email || "—"}</dd>
        <dt className="text-muted-foreground">Authorized at (UTC)</dt>
        <dd>{cur?.authorized_at_utc || "—"}</dd>
        <dt className="text-muted-foreground">Revoked at (UTC)</dt>
        <dd>{cur?.revoked_at_utc || "—"}</dd>
        <dt className="text-muted-foreground">Production capable</dt>
        <dd data-testid="pa-production-capable">{capable ? "TRUE" : "FALSE"}</dd>
      </dl>
      {(activation.blocking_reasons || []).length > 0 && (
        <div className="text-[10px] text-signal-red" data-testid="pa-blocking-reasons">
          Blocking: {activation.blocking_reasons.join(", ")}
        </div>
      )}
      <div className="flex items-center gap-2 pt-2 border-t border-border/40">
        {/* Stage 2.9A — split detector vs production-effect state.
           Detector is enabled once the feature-config gate flips
           (env), which does NOT authorize production effects. */}
        <span className={`chip !text-[9px] ${activation.feature_config_gate
          ? (capable
              ? "text-signal-green border-signal-green/40"
              : "text-[#0A84FF] border-[#0A84FF]/50")
          : "text-muted-foreground border-border/60"}`}
          data-testid="pa-momentum-detector-chip">
          {activation.feature_config_gate
            ? (capable
                ? "MOMENTUM DETECTOR: ENABLED"
                : "MOMENTUM DETECTOR: ENABLED · SHADOW")
            : "MOMENTUM DETECTOR: DISABLED"}
        </span>
        <span className={`chip !text-[9px] ${capable
          ? "text-signal-green border-signal-green/40"
          : "text-muted-foreground border-border/60"}`}
          data-testid="pa-production-effect-chip">
          {capable
            ? "PRODUCTION EFFECT: AUTHORIZED"
            : (activation.feature_config_gate && activation.realtime_sip_verified
                ? "PRODUCTION EFFECT: AWAITING OWNER AUTHORIZATION"
                : "PRODUCTION EFFECT: LOCKED")}
        </span>
        <span className="chip !text-[9px] text-muted-foreground border-border/60"
              data-testid="pa-autonomous-trading-chip">
          Autonomous Trading: LOCKED
        </span>
        {showReview && (
          <button className="chip cursor-pointer ml-auto text-[#0A84FF] border-[#0A84FF]"
                  data-testid="review-production-authorization-btn"
                  onClick={onOpenAuthorize}>
            Review Production Authorization
          </button>
        )}
        {showRevoke && (
          <button className="chip cursor-pointer ml-auto text-signal-red border-signal-red/40"
                  data-testid="revoke-production-authorization-btn"
                  disabled={revokeBusy}
                  onClick={onRevoke}>
            {revokeBusy ? "Revoking…" : "Revoke Production Authorization"}
          </button>
        )}
      </div>
    </div>
  );
}

const ACK_KEYS = [
  {k: "sip_realtime_feed_verified", label: "SIP realtime feed is verified."},
  {k: "stage_26b_validation_passed", label: "Stage 2.6B validation has passed."},
  {k: "catalyst_may_generate_alerts_after_separate_enablement", label: "Catalyst may generate production alerts only after separate feature enablement."},
  {k: "autonomous_trading_not_being_enabled", label: "Autonomous trading is not being enabled."},
  {k: "owner_can_revoke_authorization", label: "Production authorization can be revoked by the owner."},
];

function AuthorizeModal({ open, activation, onClose, onSubmit, busy, error }) {
  const [acks, setAcks] = useState({});
  const [phrase, setPhrase] = useState("");
  useEffect(() => { if (open) { setAcks({}); setPhrase(""); } }, [open]);
  const allAcked = ACK_KEYS.every((a) => acks[a.k]);
  const phraseOk = phrase === "AUTHORIZE CATALYST";
  const canSubmit = allAcked && phraseOk && !busy;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="authorize-modal">
        <DialogHeader>
          <DialogTitle>Review Production Authorization</DialogTitle>
          <DialogDescription>
            Authorization is one of the required production gates. It does NOT itself enable Catalyst Momentum and does NOT unlock autonomous trading.
          </DialogDescription>
        </DialogHeader>
        <div className="text-[11px] mono space-y-2">
          <div>SIP realtime feed: <b>{activation?.realtime_sip_verified ? "VERIFIED" : "—"}</b></div>
          <div>Stream data: <b>{activation?.stream_data_verified ? "VERIFIED" : "—"}</b></div>
          <div>Stage 2.6B validation: <b>{activation?.validation_passed ? "PASS" : "—"}</b></div>
          <div>Detector policy: <b>MATCHED</b></div>
          <div>Environment: <b>{activation?.environment}</b></div>
          <div>Current production capability: <b>{activation?.production_capable ? "CAPABLE" : "BLOCKED"}</b></div>
          <div>Autonomous trading: <b>LOCKED</b></div>
        </div>
        <div className="space-y-2 pt-2 border-t border-border/40">
          {ACK_KEYS.map((a) => (
            <label key={a.k} className="flex items-start gap-2 text-[11px]">
              <Checkbox
                checked={!!acks[a.k]}
                onCheckedChange={(v) => setAcks((p) => ({...p, [a.k]: !!v}))}
                data-testid={`ack-${a.k}`} />
              <span>{a.label}</span>
            </label>
          ))}
        </div>
        <div className="pt-2 border-t border-border/40">
          <div className="text-[10px] text-muted-foreground mb-1">Type <code>AUTHORIZE CATALYST</code> to confirm:</div>
          <Input value={phrase} onChange={(e) => setPhrase(e.target.value)}
                 data-testid="confirmation-phrase-input"
                 placeholder="AUTHORIZE CATALYST" />
        </div>
        {error && <div className="text-signal-red text-[10px]" data-testid="authorize-error">{error}</div>}
        <DialogFooter>
          <button className="chip" onClick={onClose} data-testid="authorize-cancel-btn">Cancel</button>
          <button
            className={`chip ${canSubmit ? "text-[#0A84FF] border-[#0A84FF] cursor-pointer" : "text-muted-foreground border-border/60"}`}
            disabled={!canSubmit}
            onClick={() => onSubmit(acks, phrase)}
            data-testid="authorize-submit-btn">
            {busy ? "Authorizing…" : "Authorize Production Capability"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Stage 2.7C · Revoke Production Authorization modal (shadcn) ─────
function RevokeAuthorizationModal({ open, onClose, onConfirm, busy, error }) {
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) { setReason(""); } }, [open]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="revoke-authorization-modal">
        <DialogHeader>
          <DialogTitle>Revoke Production Authorization</DialogTitle>
          <DialogDescription>
            Revoking authorization immediately blocks Catalyst production capability. Catalyst production side effects will fail closed. Alpaca connectivity is not required.
          </DialogDescription>
        </DialogHeader>
        <div className="pt-2 border-t border-border/40">
          <div className="text-[10px] text-muted-foreground mb-1">
            Optional reason (audit trail)
          </div>
          <Input value={reason} onChange={(e) => setReason(e.target.value)}
                 data-testid="revoke-reason-input"
                 placeholder="e.g. rotating owner credentials" />
        </div>
        {error && (
          <div className="text-signal-red text-[10px]" data-testid="revoke-error">{error}</div>
        )}
        <DialogFooter>
          <button className="chip" onClick={onClose}
                  data-testid="revoke-cancel-btn">Cancel</button>
          <button
            className="chip cursor-pointer text-signal-red border-signal-red/40"
            disabled={busy}
            onClick={() => onConfirm(reason)}
            data-testid="revoke-confirm-btn">
            {busy ? "Revoking…" : "Revoke Production Authorization"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ── Main page ─────────────────────────────────────────────────────────
export default function Catalysts() {
  // undefined = not yet chosen; a string means the user (or auto-default)
  // picked one. This lets us switch to LIVE RADAR on first diag load
  // WITHOUT overriding a manual tab choice made afterwards.
  const [tab, setTab] = useState(undefined);
  const [diag, setDiag] = useState(null);
  const [calendar, setCalendar] = useState([]);
  const [events, setEvents] = useState([]);
  const [outcomes, setOutcomes] = useState({});
  const [quality, setQuality] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifyError, setVerifyError] = useState(null);
  const [activation, setActivation] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revokeError, setRevokeError] = useState(null);
  const isOwner = (activation && (activation.environment != null)) ? true : false;  // fallback; real role from /auth/me below
  const [role, setRole] = useState(null);

  useEffect(() => {
    Promise.allSettled([
      api.get("/catalysts/diagnostics"),
      api.get("/catalysts/calendar"),
      api.get("/catalysts/events"),
      api.get("/catalysts/outcomes"),
      api.get("/catalysts/quality"),
      api.get("/catalysts/feed-readiness"),
      api.get("/catalysts/production-activation"),
      api.get("/auth/me"),
    ]).then(([d, c, e, o, q, r, a, u]) => {
      setDiag(d.status === "fulfilled" ? d.value.data : null);
      setCalendar(c.status === "fulfilled" ? (c.value.data.events || []) : []);
      setEvents(e.status === "fulfilled" ? (e.value.data.events || []) : []);
      const map = {};
      if (o.status === "fulfilled") {
        (o.value.data.outcomes || []).forEach((row) => {
          if (row.event_id) map[row.event_id] = row;
        });
      }
      setOutcomes(map);
      setQuality(q.status === "fulfilled" ? q.value.data : null);
      setReadiness(r.status === "fulfilled" ? r.value.data : null);
      setActivation(a.status === "fulfilled" ? a.value.data : null);
      setRole(u.status === "fulfilled" ? u.value.data?.role : null);
    });
  }, []);

  async function refreshActivation() {
    try {
      const res = await api.get("/catalysts/production-activation");
      setActivation(res.data);
    } catch (_) { /* keep prior */ }
  }

  async function submitAuthorize(acks, phrase) {
    setAuthBusy(true);
    setAuthError(null);
    try {
      const res = await api.post("/catalysts/production-activation/authorize", {
        acknowledgements: acks,
        confirmation_phrase: phrase,
      });
      if (res?.data?.authorized) {
        setAuthOpen(false);
        await refreshActivation();
      } else {
        setAuthError(res?.data?.error || "authorization refused");
      }
    } catch (err) {
      setAuthError(err?.response?.data?.detail || err?.message || "error");
    } finally {
      setAuthBusy(false);
    }
  }

  async function submitRevoke(reason) {
    setRevokeBusy(true);
    setRevokeError(null);
    try {
      await api.post("/catalysts/production-activation/revoke",
        {reason: (reason && reason.trim()) || "owner_revoke_ui"});
      setRevokeOpen(false);
      await refreshActivation();
    } catch (err) {
      setRevokeError(err?.response?.data?.detail
        || err?.message || "Revoke failed");
    } finally { setRevokeBusy(false); }
  }

  async function handleVerifySip() {
    setVerifyBusy(true);
    setVerifyError(null);
    try {
      const res = await api.post(
        "/catalysts/feed-readiness/verify-sip", { probe_seconds: 6 });
      if (res?.data?.readiness) setReadiness(res.data.readiness);
    } catch (err) {
      setVerifyError(err?.response?.data?.detail
        || err?.message || "Verification failed");
    } finally {
      setVerifyBusy(false);
    }
  }

  // P1: initial default tab — source of truth is diagnostics response.
  useEffect(() => {
    if (tab !== undefined) return;              // user already picked
    if (diag === null) return;                   // still loading
    setTab(diag.production_enabled ? "radar" : "watchlist");
  }, [diag, tab]);

  const activeTab = tab || "watchlist";
  const health = useMemo(() => categorizeNewsHealth(diag), [diag]);
  const ws = diag?.workers?.news_ws || {};

  return (
    <div className="space-y-4" data-testid="catalysts-page">
      <div className="flex items-center gap-3">
        <Activity size={18} className="text-[#0A84FF]" />
        <h1 className="mono uppercase text-sm tracking-widest">Catalyst Intelligence</h1>
        <span className="chip !text-[10px] text-signal-yellow border-signal-yellow/40">
          PHASE 1 · READ-ONLY
        </span>
        {diag && (
          <span className={`chip !text-[10px] ml-auto ${diag.production_enabled
            ? "text-signal-green border-signal-green/40"
            : "text-muted-foreground border-border/60"}`}
             data-testid="catalyst-radar-state">
            RADAR {diag.production_enabled ? "ON" : "OFF"}
          </span>
        )}
      </div>

      <div className="flex gap-2">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
                  className={`chip cursor-pointer ${activeTab === t.id ? "border-[#0A84FF] text-[#0A84FF]" : "border-border/60 text-muted-foreground"}`}
                  data-testid={`catalyst-tab-${t.id}`}>
            <t.icon size={11} className="mr-1" />
            {t.label}
          </button>
        ))}
      </div>

      <FeedReadinessCard
        readiness={readiness}
        onVerify={handleVerifySip}
        busy={verifyBusy}
        lastError={verifyError} />

      <ProductionAuthorizationCard
        activation={activation}
        isOwner={role === "owner"}
        onOpenAuthorize={() => { setAuthError(null); setAuthOpen(true); }}
        onRevoke={() => { setRevokeError(null); setRevokeOpen(true); }}
        revokeBusy={revokeBusy} />

      <AuthorizeModal
        open={authOpen}
        activation={activation}
        onClose={() => setAuthOpen(false)}
        onSubmit={submitAuthorize}
        busy={authBusy}
        error={authError} />

      <RevokeAuthorizationModal
        open={revokeOpen}
        onClose={() => setRevokeOpen(false)}
        onConfirm={submitRevoke}
        busy={revokeBusy}
        error={revokeError} />

      {activeTab === "watchlist" && (
        <div className="space-y-2" data-testid="catalyst-watchlist">
          {calendar.length === 0 ? (
            <div className="grid-cell !p-4 text-center text-[11px] text-muted-foreground">
              No curated catalyst events yet. Owners can seed the calendar via
              POST /api/catalysts/calendar (biotech readouts, PDUFA dates, IPO
              milestones, earnings, etc.).
            </div>
          ) : calendar.map((c) => (
            <div key={c.id} className="grid-cell !p-3 mono text-[11px]"
                 data-testid={`catalyst-cal-${c.id}`}>
              <div className="flex items-baseline gap-2">
                <span className="text-foreground font-medium">{c.symbol || c.expected_ticker || c.company_name}</span>
                <span className="text-muted-foreground text-[10px]">{c.event_type}</span>
                <span className="ml-auto chip !text-[9px] text-muted-foreground border-border/60">
                  {c.date_precision}
                </span>
              </div>
              <div className="mt-1 text-muted-foreground">
                {c.notes || c.company_name}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === "radar" && (
        <div className="space-y-2" data-testid="catalyst-radar">
          {events.length === 0 ? (
            health === "LIVE"
              ? <OperationalEmptyState diag={diag} />
              : <HealthBanner kind={health} diag={diag} />
          ) : events.map((e) => (
            <EventCard key={e.id || e.provider_event_id} e={e}
                        outcome={outcomes[e.id]} />
          ))}
        </div>
      )}

      {activeTab === "ipo" && (
        <div className="space-y-2" data-testid="catalyst-ipo">
          {calendar.filter((c) => c.event_type === "IPO").length === 0 ? (
            <div className="grid-cell !p-4 text-center text-[11px] text-muted-foreground">
              No IPO events curated. Add via POST /api/catalysts/calendar
              with event_type=IPO — ticker may be null for pre-listing entries.
            </div>
          ) : calendar.filter((c) => c.event_type === "IPO").map((c) => (
            <div key={c.id} className="grid-cell !p-3 mono text-[11px]">
              {c.company_name} · TICKER: {c.expected_ticker || c.symbol || "TBD"}
            </div>
          ))}
        </div>
      )}

      {activeTab === "history" && (
        <div className="space-y-2" data-testid="catalyst-history">
          {quality && quality.events_measured > 0 && (
            <div className="grid-cell !p-3 mono text-[10px]"
                 data-testid="catalyst-quality-strip">
              <div className="label-cap mb-2">Catalyst quality (descriptive · not trade P&L)</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1">
                <div>EVENTS MEASURED <span className="text-foreground">{quality.events_measured}</span></div>
                <div>COMPLETE <span className="text-signal-green">{quality.status_counts?.COMPLETE ?? 0}</span></div>
                <div>PARTIAL <span className="text-signal-yellow">{quality.status_counts?.PARTIAL ?? 0}</span></div>
                <div>PENDING <span className="text-muted-foreground">{quality.status_counts?.PENDING ?? 0}</span></div>
                <div>UNAVAILABLE <span className="text-muted-foreground">{quality.status_counts?.UNAVAILABLE ?? 0}</span></div>
                <div>MEDIAN LATENCY <span className="text-foreground">
                  {quality.median_detection_latency_ms != null
                    ? `${Math.round(quality.median_detection_latency_ms)}ms` : "—"}</span></div>
                <div>MEDIAN +30M <span className="text-foreground">
                  {fmtReturn(quality.median_return_30m_pct) || "—"}</span></div>
                <div>MEDIAN +60M <span className="text-foreground">
                  {fmtReturn(quality.median_return_60m_pct) || "—"}</span></div>
              </div>
              {quality.detector_breakdown && Object.keys(quality.detector_breakdown).length > 0 && (
                <div className="mt-2 text-muted-foreground italic">
                  Detector breakdown: {Object.entries(quality.detector_breakdown)
                    .map(([d, v]) => `${d} · ${v.count} events · median +30m ${fmtReturn(v.median_return_30m_pct) || "—"}`)
                    .join(" · ")}
                </div>
              )}
              <div className="mt-2 text-muted-foreground italic text-[9px]">
                Follow-through ≥ +2% within 30m: {quality.followthrough_ge_2pct_30m?.rate != null
                  ? `${quality.followthrough_ge_2pct_30m.rate}%` : "—"}
                {" · "}
                ≥ +5% within 60m: {quality.followthrough_ge_5pct_60m?.rate != null
                  ? `${quality.followthrough_ge_5pct_60m.rate}%` : "—"}
              </div>
            </div>
          )}
          {events.length === 0 ? (
            <div className="grid-cell !p-4 text-center text-[11px] text-muted-foreground">
              No catalyst events recorded yet.
            </div>
          ) : events.map((e) => (
            <EventCard key={e.id || e.provider_event_id} e={e}
                        outcome={outcomes[e.id]} />
          ))}
        </div>
      )}

      {diag && (
        <div className="grid-cell !p-3 mono text-[10px] text-muted-foreground"
             data-testid="catalyst-diag-strip">
          news: {diag.news_provider?.state} · market: {diag.market_provider?.state} · calendar: {diag.calendar_provider?.state} · AI: {diag.rapid_ai?.enabled ? "ON" : "OFF"} · outcomes: <span data-testid="strip-outcomes">{diag.outcome_worker?.state || "OFF"}</span> · isolation: {diag.isolation?.catalyst_opportunities_created ?? 0} opps / {diag.isolation?.catalyst_broker_orders_created ?? 0} orders · {diag.autonomous_trading}
          {" · "}EVENTS SESSION: <span data-testid="strip-events">{ws.events_received ?? "—"}</span>
          {" · "}LAST EVENT: <span data-testid="strip-last-event">{fmtEpoch(ws.last_event_at)}</span>
          {" · "}RECONNECTS: <span data-testid="strip-reconnects">{ws.reconnect_count ?? "—"}</span>
        </div>
      )}
    </div>
  );
}
